import { create, StateCreator } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { Batter } from '@/components/CakeMaker/data/batters'
import type { Weight } from '@/components/CakeMaker/data/weights'
import type { Shape } from '@/components/CakeMaker/data/shapes'
import { Filling } from '@/components/CakeMaker/data/fillings'
import { Topping } from '@/components/CakeMaker/data/toppings'
import { Additional } from '@/components/CakeMaker/data/additional'
import { THEME_PRICE } from '@/config/constants'
import { Candy, candies } from '@/components/CakeMaker/data/candies'
import { Order } from '@/@types'

const CANDIES_MAX_QUANTITY = 800

export type Delivery = {
  address: string
  city: string
  number: string
  state: string
  zipCode: string
  price: number
  neighborhood: string
  complement: string
}

type CakeMakerActions = {
  goToNextStep: () => void
  goToPreviousStep: () => void
  goToStep: (step: number) => void
  setOrderDate: (date: Date) => void
  setTheme: (theme: string) => void
  setBatter: (batter: Batter) => void
  setWeight: (weight: Weight) => void
  setShape: (shape: Shape) => void
  setFilling: (filling: Filling) => void
  setTopping: (topping: Topping) => void
  setAdditional: (additional: Additional) => void
  addCandy: (candy: Candy) => void
  subtractCandy: (candy: Candy) => void
  setDelivery: (delivery: Delivery | undefined) => void
  reset: () => void
  persistOrder: (order: Order) => void
}

type CakeMakerState = {
  storeOrders: Order[]
  actions: CakeMakerActions
  step: number

  orderDate: Date | undefined
  theme: string
  batter: Batter | undefined
  weight: Weight | undefined
  shape: Shape | undefined
  fillings: Filling[]
  topping: Topping | undefined
  additional: Additional[]
  candies: Candy[]
  delivery: Delivery | undefined
}

const createCakeMakerActions: StateCreator<
  CakeMakerState,
  [],
  [],
  CakeMakerActions
> = (set) => ({
  goToNextStep: () =>
    set((state) => ({ step: state.step === 5 ? 5 : state.step + 1 })),
  goToPreviousStep: () =>
    set((state) => ({ step: state.step === 1 ? 1 : state.step - 1 })),
  // Só permite retroceder: avançar aqui pularia a validação de cada etapa.
  goToStep: (step: number) =>
    set((state) => ({
      step: step < state.step && step >= 1 ? step : state.step,
    })),
  setOrderDate: (date: Date) => set({ orderDate: date }),
  setTheme: (theme: string) => set({ theme }),
  setBatter: (batter: Batter) =>
    set((state) => ({
      batter: state.batter?.id === batter?.id ? undefined : batter,
    })),
  setWeight: (weight: Weight) =>
    set((state) => ({
      weight: state.weight?.id === weight?.id ? undefined : weight,
    })),
  setShape: (shape: Shape) =>
    set((state) => ({
      shape: state.shape?.id === shape?.id ? undefined : shape,
    })),
  setFilling: (filling: Filling) =>
    set((state) => {
      const index = state.fillings.findIndex((f) => f.id === filling?.id)

      if (index > -1)
        return { fillings: state.fillings.filter((_, i) => i !== index) }

      const newFillings = [...state.fillings]

      if (newFillings.length === 2) newFillings.shift()

      newFillings.push(filling)

      return { fillings: newFillings }
    }),
  setTopping: (topping: Topping) =>
    set((state) => ({
      topping: state.topping?.id === topping.id ? undefined : topping,
    })),
  setAdditional: (additional: Additional) =>
    set((state) => {
      const index = state.additional.findIndex((a) => a.id === additional?.id)

      if (index > -1)
        return { additional: state.additional.filter((_, i) => i !== index) }

      return { additional: [...state.additional, additional] }
    }),
  addCandy: (candy: Candy) =>
    set((state) => {
      const index = state.candies.findIndex((c) => c.id === candy.id)

      if (index === -1)
        return { candies: [...state.candies, { ...candy, quantity: 1 }] }

      if (state.candies[index].quantity === CANDIES_MAX_QUANTITY)
        return { candies: state.candies }

      const newCandies = [...state.candies]

      newCandies[index].quantity =
        newCandies[index].quantity! + candy.incrementStep

      return { candies: newCandies }
    }),
  subtractCandy: (candy: Candy) =>
    set((state) => {
      const index = state.candies.findIndex((c) => c.id === candy.id)

      if (index === -1) return { candies: state.candies }

      const newCandies = [...state.candies]

      if (state.candies[index].quantity === 0) {
        return { candies: newCandies }
      }

      newCandies[index].quantity =
        newCandies[index].quantity! - candy.incrementStep

      return { candies: newCandies }
    }),
  setDelivery: (delivery: Delivery | undefined) => set({ delivery }),
  reset: () =>
    set({
      step: 1,
      orderDate: undefined,
      theme: '',
      batter: undefined,
      weight: undefined,
      shape: undefined,
      fillings: [],
      topping: undefined,
      additional: [],
      candies: [],
      delivery: undefined,
    }),
  persistOrder: (order: Order) => {
    set((state) => ({
      storeOrders: [...state.storeOrders, { ...order, orderedAt: new Date() }],
    }))
  },
})

export const useCakeMakerState = create<CakeMakerState>()(
  persist(
    (...args) => ({
      storeOrders: [],
      actions: createCakeMakerActions(...args),
      step: 1,

      orderDate: undefined,
      theme: '',
      batter: undefined,
      weight: undefined,
      shape: undefined,
      fillings: [],
      topping: undefined,
      additional: [],
      candies,
      delivery: undefined,
    }),
    {
      name: '@orders',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ storeOrders: state.storeOrders }),
    },
  ),
)

export const useCakeMakerActions = () =>
  useCakeMakerState((state) => state.actions)

export const useCakeMakerTotal = () =>
  useCakeMakerState((state) => {
    const multiplicationFactor = state.weight?.multiplication_factor || 0

    const themePrice = state.theme.trim().length ? THEME_PRICE : 0

    const batterPrice = (state.batter?.price || 0) * multiplicationFactor

    const fillingsPrice =
      state.fillings.reduce((acc, filling) => {
        return acc + (filling.price || 0)
      }, 0) * multiplicationFactor

    const toppingPrice = (state.topping?.price || 0) * multiplicationFactor

    const additionalPrice =
      state.additional.reduce((acc, additional) => {
        return acc + (additional.price || 0)
      }, 0) * multiplicationFactor

    const candiesPrice = state.candies.reduce((acc, candy) => {
      return acc + (candy.price || 0) * candy.quantity
    }, 0)

    const deliveryPrice = state.delivery?.price || 0

    return (
      batterPrice +
      themePrice +
      fillingsPrice +
      toppingPrice +
      additionalPrice +
      candiesPrice +
      deliveryPrice
    )
  })
